import { yupResolver } from "@hookform/resolvers/yup";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Collapse, Divider, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { getClientFullName } from "@zfp-akademie-a-s/contract-details";
import { Alert } from "Components/_Shared/Alert";
import { BlSkeleton } from "Components/_Shared/BlSkeleton";
import { Button } from "Components/_Shared/Buttons/Button";
import { ClientCardDetail } from "Components/_Shared/Client/ClientCardDetail";
import { ClientDetailError } from "Components/_Shared/Client/ClientDetailError";
import { ClientsSearch } from "Components/_Shared/Client/ClientsSearch";
import { DialogModal } from "Components/_Shared/DialogModal";
import { PhoneInput } from "Components/_Shared/Inputs/PhoneInput";
import { TextInput } from "Components/_Shared/Inputs/TextInput";
import { BlDropdown } from "Forms/Components/BlDropdown";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { DatePicker } from "Forms/Components/DatePicker";
import { StyledFormControl, StyledLabelAdornment } from "Forms/Components/FormStyles";
import { type ClientDetailsFormData, clientDetailsFormSchema } from "Forms/Schemas/clientDetailsFormSchema";
import {
  getClientDetailQueryKey,
  getGetAnalysisClientsQueryKey,
  getGetAnalysisQueryKey,
  useAssignClientToAnalysis,
  useClientDetail,
  useListCountries,
  useUpdateClient,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { ClientDetailBadRequestErrorCode, ClientType, RelatedClientDetailType } from "Infrastructure/ApiZfp/Schema";
import type { AssignClientBadRequestError, Client, SearchedClient } from "Infrastructure/ApiZfp/Schema";
import { AssignClientBadRequestErrorCode } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import type { NonRelatedClientDetail } from "Infrastructure/Utils/ClientUtils";
import { CLIENT_DETAIL_RETRY, getMinBirthDate, getSearchedClientName, selectClientDetailClient } from "Infrastructure/Utils/ClientUtils";
import { format as formatDate } from "date-fns";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "./AnalysisDetailContext";

const clientToFormData = (client: NonRelatedClientDetail): ClientDetailsFormData => ({
  firstName: client.firstName,
  lastName: client.surname,
  titlePrefix: client.titlePrefix || "",
  titlePostfix: client.titlePostfix || "",
  birthDate: client.birthDate || ("" as any),
  email: client.email ?? "",
  phone: client.phoneNationalNumber ?? "",
  prefix: client.phonePrefix ?? "+420",
  countryCode: client.address?.countryCode ?? "CZE",
  city: client.address?.city ?? "",
  postalCode: client.address?.postalCode?.replace(/\s/g, "") ?? "",
  street: client.address?.street ?? "",
  streetNumber: client.address?.streetNumber ?? "",
  houseNumber: client.address?.houseNumber ?? "",
  personalIdentificationNumber:
    "personalIdentificationNumber" in client && client.personalIdentificationNumber ? client.personalIdentificationNumber : "",
});

type Props = {
  readonly onClose: () => void;
  readonly clientGuid: Client["guid"];
};

type FormProps = {
  readonly client: NonRelatedClientDetail;
  readonly onClose: () => void;
};

export const EditClientForm: React.FC<Props> = ({ onClose, clientGuid }) => {
  const { t } = useResource();
  const {
    data: client,
    isPending: isClientPending,
    isError: isClientError,
  } = useClientDetail(clientGuid, {
    query: { staleTime: 0, select: selectClientDetailClient, retry: CLIENT_DETAIL_RETRY },
  });

  if (isClientPending) {
    return (
      <Stack spacing={Spacing.XL}>
        <BlSkeleton variant="rectangular" height={56} />
        <BlSkeleton variant="rectangular" height={56} />
        <BlSkeleton variant="rectangular" height={56} />
        <BlSkeleton variant="rectangular" height={56} />
        <BlSkeleton variant="rectangular" height={56} />
        <BlSkeleton variant="rectangular" height={56} />
      </Stack>
    );
  }

  if (isClientError) {
    return <Alert variant="error" text={t(Resources.Clients.LoadError)} />;
  }

  if (client.type === RelatedClientDetailType.related) {
    return null;
  }

  return <EditClientFormContent key={client.guid} client={client} onClose={onClose} />;
};

const EditClientFormContent: React.FC<FormProps> = ({ client, onClose }) => {
  const { readOnly, versionId, analysisId } = useContext(AnalysisDetailContext);
  const [selectedSearchedClient, setSelectedSearchedClient] = useState<SearchedClient | null>(null);
  const [showAssignSearch, setShowAssignSearch] = useState(false);
  const { t } = useResource();
  const queryClient = useQueryClient();

  const selectedSearchedClientId = selectedSearchedClient?.guid ?? null;
  const isSearchedClientSelected = !!selectedSearchedClientId;

  const isExistingClient = client.type === ClientType.existing;

  // Only for the confirm dialog's name/birth date and the incomplete-client state - the preview
  // itself is rendered by ClientCardDetail, which fetches the same query key and dedupes with this.
  const { data: selectedClientData, isError: isSelectedClientError } = useClientDetail(selectedSearchedClientId ?? "", {
    query: { enabled: isSearchedClientSelected, staleTime: 0, select: selectClientDetailClient, retry: CLIENT_DETAIL_RETRY },
  });

  // The parent mounts this component with key={client.guid}, so `defaultValues` is the single source
  // of truth - deliberately no reset() on refetch, which would discard in-progress edits.
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ClientDetailsFormData>({
    resolver: yupResolver(clientDetailsFormSchema(t)),
    mode: "onBlur",
    defaultValues: clientToFormData(client),
  });

  const handleSelect = (client: SearchedClient | null) => {
    setSelectedSearchedClient(client);
  };

  const [assignConfirmOpen, setAssignConfirmOpen] = useState(false);

  const { data: countriesData } = useListCountries();
  const { mutateAsync: updateClient, isPending: isUpdatingClient } = useUpdateClient();
  const { mutateAsync: assignClient, isPending: isAssigningClient } = useAssignClientToAnalysis();

  const invalidateClientQueries = () => {
    queryClient.invalidateQueries({ queryKey: getGetAnalysisClientsQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getClientDetailQueryKey(client.guid) });
    queryClient.invalidateQueries({ queryKey: getGetAnalysisQueryKey(analysisId, versionId) });
  };

  const onUpdateClient = async (data: ClientDetailsFormData) => {
    await updateClient({
      clientGuid: client.guid,
      data: {
        name: data.firstName,
        surname: data.lastName,
        titlePrefix: data.titlePrefix || null,
        titlePostfix: data.titlePostfix || null,
        birthDate: data.birthDate instanceof Date ? data.birthDate.toISOString() : data.birthDate || null,
        email: data.email,
        phonePrefix: data.prefix,
        phoneNationalNumber: data.phone,
        addressStreet: data.street || null,
        addressStreetNumber: data.streetNumber || null,
        addressHouseNumber: data.houseNumber || null,
        addressCity: data.city || null,
        addressPostalCode: data.postalCode || null,
        addressCountry: data.countryCode || null,
      },
    });
    invalidateClientQueries();
  };

  const handleAssign = async () => {
    try {
      await assignClient({ analysisId, versionId, data: { clientGuid: selectedSearchedClientId! } });
      invalidateClientQueries();
      toast.success(t(Resources.Clients.AssignSuccess));
      onClose();
    } catch (error) {
      const code = (error as AssignClientBadRequestError)?.code;
      switch (code) {
        case AssignClientBadRequestErrorCode.client_not_local:
          toast.error(t(Resources.Clients.AssignErrors.ClientNotLocal));
          break;
        case AssignClientBadRequestErrorCode.client_not_allowed:
          toast.error(t(Resources.Clients.AssignErrors.ClientNotAllowed));
          break;
        case AssignClientBadRequestErrorCode.analysis_already_exists_for_client:
          toast.error(t(Resources.Clients.AssignErrors.AnalysisAlreadyExists));
          break;
        case AssignClientBadRequestErrorCode.nsure_self_dealing_forbidden:
          toast.error(t(Resources.Clients.AssignErrors.NsureSelfDealingForbidden));
          break;
        default:
          toast.error(t(Resources.Clients.AssignError));
      }
    }
  };

  const onSubmit = async (data: ClientDetailsFormData) => {
    try {
      await onUpdateClient(data);
      toast.success(t(Resources.Clients.EditSuccess));
      onClose();
    } catch {
      toast.error(t(Resources.Clients.EditError));
    }
  };

  const mangoUpCardUrl = "mangoUpCardUrl" in client && client.mangoUpCardUrl ? client.mangoUpCardUrl : null;
  // The searched-client preview no longer renders through this form, so it can't disable it either
  const isDisabled = readOnly || isExistingClient;

  return (
    <Stack pb={Spacing.L} spacing={Spacing.XL}>
      {!readOnly &&
        (isExistingClient ? (
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={Spacing.S}>
              <Typography fontWeight={500} variant="body2">
                {t(Resources.Clients.MangoLinkedTitle)}
              </Typography>
              <Typography
                component="a"
                href={mangoUpCardUrl ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                color="primary"
                fontWeight={500}
                sx={{ display: "flex", alignItems: "center", gap: 0.5, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              >
                {t(Resources.Clients.UpdateInMango)}
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </Typography>
            </Stack>
            <StyledFormControl>
              <TextInput
                disabled
                value={getClientFullName({
                  titlePrefix: client.titlePrefix,
                  titlePostfix: client.titlePostfix,
                  name: client.firstName ?? "",
                  surname: client.surname ?? "",
                })}
              />
            </StyledFormControl>
            <Box mt={Spacing.XXL}>
              <Divider />
            </Box>
          </Box>
        ) : (
          <Stack spacing={Spacing.XXL}>
            <Alert
              variant="warning"
              title={t(Resources.Clients.MangoNotLinkedTitle)}
              text={t(Resources.Clients.MangoNotLinkedText)}
              {...(!showAssignSearch && {
                action: (
                  <Button variant="outlined" color="warning" size="small" onClick={() => setShowAssignSearch(true)}>
                    {t(Resources.Clients.AssignClient)}
                  </Button>
                ),
              })}
            />
            <Collapse in={showAssignSearch} unmountOnExit>
              <Stack>
                <Typography variant="subtitle1" fontWeight={600}>
                  {t(Resources.Clients.ConnectClientToMango)}
                </Typography>
                <ClientsSearch onSelect={handleSelect} />
              </Stack>
            </Collapse>
            <Divider />
          </Stack>
        ))}

      {selectedSearchedClient && isSelectedClientError ? (
        <Box pt={Spacing.XL}>
          <ClientDetailError clientName={getSearchedClientName(selectedSearchedClient)} />
        </Box>
      ) : (
        <Stack justifyContent={"space-between"} height={"100%"}>
          <Box>
            {isSearchedClientSelected && selectedSearchedClientId ? (
              <Box pt={Spacing.XL}>
                <ClientCardDetail clientGuid={selectedSearchedClientId} rows={[]} />
              </Box>
            ) : (
              <>
                <Grid container spacing={Spacing.XL}>
                  <Grid size={12}>
                    <Typography mb={Spacing.M} variant="subtitle1" fontWeight={600}>
                      {t(Resources.Common.PersonalInfo)}
                    </Typography>
                  </Grid>
                  {isExistingClient && (
                    <Grid size={12}>
                      <BlFormInput
                        disabled={isDisabled}
                        control={control}
                        name="personalIdentificationNumber"
                        label={t(Resources.Clients.Field.PersonalIdentificationNumber)}
                        errors={errors}
                      />
                    </Grid>
                  )}
                  <Grid size={6}>
                    <BlFormInput
                      disabled={isDisabled}
                      control={control}
                      name="firstName"
                      label={t(Resources.Clients.Field.FirstName)}
                      errors={errors}
                      labelEndAdornment={<StyledLabelAdornment />}
                    />
                  </Grid>
                  <Grid size={6}>
                    <BlFormInput
                      control={control}
                      disabled={isDisabled}
                      name="lastName"
                      label={t(Resources.Clients.Field.LastName)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={3}>
                    <BlFormInput
                      disabled={isDisabled}
                      control={control}
                      name="titlePrefix"
                      label={t(Resources.Clients.Field.AcademicDegreePrefix)}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={3}>
                    <BlFormInput
                      disabled={isDisabled}
                      control={control}
                      name="titlePostfix"
                      label={t(Resources.Clients.Field.AcademicDegreePostfix)}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={6}>
                    <DatePicker
                      control={control}
                      name="birthDate"
                      label={t(Resources.Clients.Field.BirthDate)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      disabled={isDisabled}
                      minDate={getMinBirthDate()}
                      disableFuture
                    />
                  </Grid>
                  <Grid size={6}>
                    <BlFormInput
                      control={control}
                      disabled={isDisabled}
                      name="email"
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      label={t(Resources.Clients.Field.Email)}
                    />
                  </Grid>
                  <Grid size={6}>
                    <PhoneInput
                      control={control}
                      disabled={isDisabled}
                      numberInputName="phone"
                      prefixInputName="prefix"
                      label={t(Resources.Common.Phone)}
                      errors={errors}
                      labelEndAdornment={<StyledLabelAdornment />}
                    />
                  </Grid>
                  <Grid size={12}>
                    <Divider />
                  </Grid>
                </Grid>
                <Box mt={Spacing.XL}>
                  <Grid container spacing={Spacing.XL}>
                    <Grid size={12}>
                      <Typography fontWeight={600} variant="subtitle1">
                        {t(Resources.Common.Address)}
                      </Typography>
                    </Grid>
                    <Grid size={8}>
                      <BlFormInput
                        control={control}
                        disabled={isDisabled}
                        name="street"
                        label={t(Resources.Clients.Field.Street)}
                        errors={errors}
                      />
                    </Grid>
                    <Grid size={2}>
                      <BlFormInput
                        control={control}
                        name="houseNumber"
                        label={t(Resources.Clients.Field.HouseNumber)}
                        errors={errors}
                        mask={/^\d+$/}
                        disabled={isDisabled}
                      />
                    </Grid>
                    <Grid size={2}>
                      <BlFormInput
                        control={control}
                        name="streetNumber"
                        label={t(Resources.Clients.Field.StreetNumber)}
                        errors={errors}
                        mask={/^\d+$/}
                        disabled={isDisabled}
                      />
                    </Grid>
                    <Grid size={6}>
                      <BlFormInput
                        control={control}
                        disabled={isDisabled}
                        name="city"
                        label={t(Resources.Clients.Field.City)}
                        errors={errors}
                      />
                    </Grid>
                    <Grid size={6}>
                      <BlFormInput
                        control={control}
                        disabled={isDisabled}
                        name="postalCode"
                        label={t(Resources.Clients.Field.PostalCode)}
                        errors={errors}
                        mask="000 00"
                      />
                    </Grid>
                    <Grid size={12}>
                      <BlDropdown
                        control={control}
                        disabled={isDisabled}
                        name="countryCode"
                        label={t(Resources.Clients.Field.Country)}
                        errors={errors}
                        codeList={countriesData || []}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}
          </Box>
          <Box p={Spacing.XL} sx={{ display: "flex", justifyContent: "flex-end", textAlign: "right" }}>
            <Button color="secondary" sx={{ marginRight: Spacing.XL }} onClick={onClose}>
              {t(Resources.Common.Close)}
            </Button>
            {!isExistingClient && !readOnly && (
              <Button
                disabled={(!isSearchedClientSelected && !isValid) || isUpdatingClient || isAssigningClient}
                loading={isUpdatingClient}
                onClick={isSearchedClientSelected ? () => setAssignConfirmOpen(true) : handleSubmit(onSubmit)}
                type="submit"
              >
                {isSearchedClientSelected ? t(Resources.Clients.AssignClient) : t(Resources.Common.Save)}
              </Button>
            )}
          </Box>
        </Stack>
      )}
      <DialogModal
        isOpen={assignConfirmOpen}
        onClose={() => setAssignConfirmOpen(false)}
        title={t(Resources.Clients.AssignConfirmTitle)}
        subtitle={
          <>
            {t(Resources.Clients.AssignConfirmTextPrefix)}{" "}
            <Box component="span" fontWeight="bold">
              {getClientFullName({
                titlePrefix:
                  selectedClientData && selectedClientData.type !== RelatedClientDetailType.related
                    ? selectedClientData.titlePrefix
                    : undefined,
                titlePostfix:
                  selectedClientData && selectedClientData.type !== RelatedClientDetailType.related
                    ? selectedClientData.titlePostfix
                    : undefined,
                name: selectedClientData?.firstName ?? "",
                surname: selectedClientData?.surname ?? "",
              })}
              {selectedClientData?.birthDate && ` (${formatDate(new Date(selectedClientData.birthDate), "d. M. yyyy")})`}
            </Box>{" "}
            {t(Resources.Clients.AssignConfirmTextSuffix)}
          </>
        }
        primaryAction={
          <Button onClick={handleAssign} disabled={isAssigningClient} loading={isAssigningClient}>
            {t(Resources.Clients.AssignClient)}
          </Button>
        }
      />
    </Stack>
  );
};
